import { Add } from '@mui/icons-material';
import { Card, Grid2, styled } from '@mui/material';
import { type FC, useEffect, useState } from 'react';
import type { VisitAmsPartModel, VisitFaultModel } from '../../../models';
import { AmsPartCard } from '../../Cards';

type AmsPartsHandleProps = {
  fault: VisitFaultModel;
  onUpdate: (updatedAmsPartObject: VisitAmsPartModel) => void;
  onUpdateExisting: (updatedAmsPartObject: VisitAmsPartModel, oldId: string) => void;
};

const StyledCard = styled(Card)(
  ({ theme }) => `
    border-radius: 16px;
    padding: 16px;
    border: 1px solid ${theme.colors.alpha.black[30]};
    background-color: transparent;
    box-shadow: none;

    &:hover {
      border-color: ${theme.colors.primary.main}
    }
`
);

function getAmsPartCard(
  newPartObject: VisitAmsPartModel,
  selectPart: (AmsPartData: VisitAmsPartModel) => void,
  onUpdateExisting: (updatedAmsPartObject: VisitAmsPartModel, oldId: string) => void
) {
  return (
    <AmsPartCard
      part={newPartObject}
      selectPart={(AmsPartData: VisitAmsPartModel) => selectPart(AmsPartData)}
      updateExistingPart={onUpdateExisting}
    />
  );
}

export const AmsPartsHandle: FC<AmsPartsHandleProps> = ({ fault, onUpdate, onUpdateExisting }) => {
  const newPartObject: VisitAmsPartModel = {
    id: `${Date.now()}.${Math.random().toString().substring(2)}`,
    amsPartId: 'newPart',
    amsPartType: '',
    quantity: 1,
    timeSpent: 60
  };
  const [blankPart, setBlankPart] = useState<boolean>(false);
  const [canAdd, setCanAdd] = useState<boolean>(!blankPart);

  const selectPart = (AmsPartData: VisitAmsPartModel): void => {
    const isValid = AmsPartData.amsPartType !== 'deleted' && AmsPartData.amsPartId !== '';

    if (isValid) {
      // Do not modify local state here; just push the update up
      onUpdate(AmsPartData);
    }
  };

  useEffect(() => {
    if (!blankPart) return;

    // If the part with ID 'newPart' is now in the list, the blank was submitted
    const newPartCommitted = fault.visitAmsParts.some((p) => p.id === 'newPart');

    if (newPartCommitted) {
      setBlankPart(false);
      setCanAdd(true);
    }
  }, [fault.visitAmsParts, blankPart]);

  const newPart = (): void => {
    if (blankPart) return; // Already one blank card open
    setBlankPart(true);
    setCanAdd(false);
  };
  return (
    <Grid2 container columns={2} direction={'row'} spacing={3}>
      {fault.visitAmsParts?.map((part, i) => {
        if (part.id.includes('.')) return null;
        return (
          <Grid2 size={{ xs: 2, lg: 1 }} key={part.amsPartId + i}>
            <AmsPartCard
              existing
              part={{ ...part }}
              selectPart={(AmsPartData: VisitAmsPartModel) => selectPart(AmsPartData)}
              updateExistingPart={onUpdateExisting}
            />
          </Grid2>
        );
      })}
      {blankPart && (
        <Grid2 size={{ xs: 2, lg: 1 }}>{getAmsPartCard(newPartObject, selectPart, onUpdateExisting)}</Grid2>
      )}
      {!!canAdd && (
        <Grid2 size={{ xs: 2, lg: 1 }}>
          <StyledCard
            onClick={() => newPart()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              minHeight: '361px',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            <Add fontSize={'large'} />
          </StyledCard>
        </Grid2>
      )}
    </Grid2>
  );
};
